import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import {
  createExtranetUser,
  createExtranetAgency,
  getExtranetUser,
  listExtranetAgencies,
  updateExtranetUser,
  validateExtranetAgencyName,
  type ExtranetAgency,
  type ExtranetUser,
} from '../../services/userAdministration';
import { Modal } from '../../components/common/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { uploadFileToDmsFolder } from '../../services/fileManagement';
import { setFlashToast } from '../../utils/flashToast';
import { alSelClass } from '../../utils/alSelClass';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { contactNumberError } from '../../utils/caseFieldValidation';
import { digitsOnlyMax10 } from '../../utils/userFieldValidation';
import { validateProfileImageFile } from '../../utils/profileImageUpload';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';
import { useDmsImageObjectUrl } from '../../hooks/useDmsImageObjectUrl';

function isValidEmail(s: string): boolean {
  const t = s.trim();
  return t.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

function nowLabel() {
  return new Date().toLocaleDateString();
}

function makeEmpty(): ExtranetUser {
  return {
    id: `eu-${Math.floor(Math.random() * 9000 + 1000)}`,
    agencyName: '',
    companyCode: '',
    brn: '',
    displayName: '',
    email: '',
    contactPersonName: '',
    contactPersonDesignation: '',
    contactNumber: '',
    status: 'Active',
    profilePicFileRef: '',
    profilePicFileChanged: false,
    createdAt: nowLabel(),
    createdBy: 'System Admin',
    updatedAt: nowLabel(),
    updatedBy: 'System Admin',
  };
}

const HARD_CODED_AGENCIES: ExtranetAgency[] = [
  { id: 1001, name: 'Ceylon Petroleum Corporation', createdAt: 'Nov 12, 2023', createdBy: 'Mr. Raj Jayasinghe' },
  { id: 1002, name: 'Sri Lanka Ports Authority', createdAt: 'Oct 20, 2023', createdBy: 'Mr. Raj Jayasinghe' },
  { id: 1003, name: 'National Water Supply & Drainage Board', createdAt: 'Sep 15, 2023', createdBy: 'Mr. Raj Jayasinghe' },
  { id: 1004, name: 'Sri Lanka Insurance Corporation', createdAt: 'Aug 05, 2023', createdBy: 'Mr. Raj Jayasinghe' },
  { id: 1005, name: 'Airport & Aviation Services Sri Lanka', createdAt: 'Jan 10, 2023', createdBy: 'Mr. Raj Jayasinghe' },
].sort((a, b) => a.name.localeCompare(b.name));

export default function ExtranetUserFormPage() {
  const { t } = useTranslation();
  const { userId } = useParams();
  const isEdit = !!userId;
  const nav = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState<ExtranetUser>(() => makeEmpty());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState<ExtranetAgency[]>(() => HARD_CODED_AGENCIES);
  const [uploading, setUploading] = useState(false);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState<string>('');
  const remoteProfilePicUrl = useDmsImageObjectUrl(user.profilePictureIconUrl, user.profilePictureIconFullpath);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const savingRef = useRef(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [addAgencyOpen, setAddAgencyOpen] = useState(false);
  const [newAgencyName, setNewAgencyName] = useState('');
  const [addAgencyErr, setAddAgencyErr] = useState<string | null>(null);
  const [addAgencySaving, setAddAgencySaving] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isEdit || !userId) return;
    let alive = true;
    void getExtranetUser(userId).then((r) => {
      if (!alive) return;
      if (r.success) setUser(r.data);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [isEdit, userId]);

  useEffect(() => {
    let alive = true;
    void listExtranetAgencies().then((r) => {
      if (!alive) return;
      if (r.success) setAgencyOptions(r.data);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    // Backfill agencyId for edit-mode loads that only have agencyName.
    if (!user.agencyName.trim() || user.agencyId != null) return;
    const match = agencyOptions.find((a) => a.name === user.agencyName) ?? null;
    if (!match) return;
    setUser((u) => ({ ...u, agencyId: match.id }));
  }, [agencyOptions, user.agencyId, user.agencyName]);

  useEffect(() => {
    return () => {
      if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
    };
  }, [profilePreviewUrl]);

  const agencyNames = useMemo(() => {
    const names = agencyOptions.map((a) => a.name);
    const current = user.agencyName.trim();
    if (current && !names.includes(current)) {
      return [...names, current].sort((a, b) => a.localeCompare(b));
    }
    return names;
  }, [agencyOptions, user.agencyName]);

  const autoDisplayName = useMemo(() => user.contactPersonName.replace(/\s+/g, ' ').trim(), [user.contactPersonName]);

  const displayNameToSave = useMemo(() => {
    return user.displayName.trim().length ? user.displayName : autoDisplayName;
  }, [autoDisplayName, user.displayName]);

  const requiredSpecs: RequiredFieldSpec[] = useMemo(
    () => [
      { key: 'agencyName', label: t('admin.extranetUserForm.extranetAgencyName'), value: user.agencyName, elementId: 'extranet-agencyName' },
      { key: 'email', label: t('profile.emailUsername'), value: user.email.trim(), elementId: 'extranet-email' },
      { key: 'contactPersonName', label: t('admin.extranetUserForm.agencyUserFullName'), value: user.contactPersonName.trim(), elementId: 'extranet-contactPersonName' },
      { key: 'contactNumber', label: t('profile.contactNumber'), value: user.contactNumber?.trim() ?? '', elementId: 'extranet-contactNumber' },
    ],
    [t, user.agencyName, user.email, user.contactPersonName, user.contactNumber]
  );

  function clearReqError(key: string) {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  }

  async function handleSave() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    let keepLocked = false;
    try {
      const errs = validateRequired(requiredSpecs);
      const merged: RequiredErrors = { ...errs };
      const maxExceeded = t('profile.maxCharLimit');

      if (user.contactPersonName.trim().length > 255) merged.contactPersonName = maxExceeded;
      const emailTrimmed = user.email.trim();
      if (emailTrimmed.length > 255) merged.email = maxExceeded;
      else if (!isValidEmail(user.email)) merged.email = t('admin.shared.invalidEmail');
      if ((user.companyCode?.trim().length ?? 0) > 255) merged.companyCode = maxExceeded;
      if ((user.brn?.trim().length ?? 0) > 255) merged.brn = maxExceeded;
      if ((user.contactPersonDesignation?.trim().length ?? 0) > 255) merged.contactPersonDesignation = maxExceeded;

      const contactDigits = digitsOnlyMax10(user.contactNumber ?? '');
      if (!contactDigits) merged.contactNumber = t('admin.extranetUserForm.contactRequired');
      else {
        const contactErr = contactNumberError(contactDigits);
        if (contactErr) merged.contactNumber = contactErr;
      }

      if (user.agencyId == null || user.agencyId <= 0) {
        merged.agencyName = t('admin.extranetUserForm.selectValidAgency');
      }

      if (Object.keys(merged).length) {
        setReqErrors(merged);
        focusFirstInvalid(requiredSpecs, merged);
        return;
      }

      const next: ExtranetUser = {
        ...user,
        contactPersonName: user.contactPersonName.trim(),
        email: user.email.trim(),
        companyCode: user.companyCode?.trim() ?? '',
        brn: user.brn?.trim() ?? '',
        contactPersonDesignation: user.contactPersonDesignation?.trim() ?? '',
        contactNumber: contactDigits,
        displayName: autoDisplayName,
      };

      const r = isEdit && userId ? await updateExtranetUser(userId, next) : await createExtranetUser(next);
      if (!r.success) {
        nav(location.pathname, { replace: true, state: { toast: { message: r.message ?? t('admin.shared.requestFailed'), variant: 'error' } } });
        return;
      }

      setFlashToast({
        message: isEdit ? t('admin.extranetUserForm.updateSuccess') : t('admin.extranetUserForm.createSuccess'),
        variant: 'success',
      });
      nav('/admin/extranet-users');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        <Link to="/admin/extranet-users">{t('admin.extranetUserForm.allExtranetUsers')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('admin.extranetUserForm.updateTitle') : t('admin.extranetUserForm.addTitle')}
      </div>

      <div className="pg-title">{isEdit ? t('admin.extranetUserForm.updateTitle') : t('admin.extranetUserForm.addTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--bl)', fontWeight: 800 }}>{t('admin.extranetUserForm.sectionTitle')}</div>

        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}

          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 18 }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 10,
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {profilePreviewUrl ? (
                <img src={profilePreviewUrl} alt={t('profile.profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : remoteProfilePicUrl ? (
                <img src={remoteProfilePicUrl} alt={t('profile.profileImageAlt')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden="true">
                  <path d="M3 21h18" />
                  <path d="M6 21V7l6-4 6 4v14" />
                  <path d="M9 21v-8h6v8" />
                </svg>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.currentTarget.files?.[0] ?? null;
                if (!f) return;
                const validation = validateProfileImageFile(f);
                if (!validation.ok) {
                  setFlashToast({ message: validation.message, variant: 'error' });
                  e.currentTarget.value = '';
                  return;
                }

                if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
                const nextPreview = URL.createObjectURL(f);
                setProfilePreviewUrl(nextPreview);

                setUploading(true);
                void (async () => {
                  try {
                    const res = await uploadFileToDmsFolder(f);
                    if (!res.success) {
                      setFlashToast({ message: res.message ?? t('admin.shared.imageUploadFailed'), variant: 'error' });
                      URL.revokeObjectURL(nextPreview);
                      setProfilePreviewUrl('');
                      return;
                    }
                    setUser((u) => ({ ...u, profilePicFileRef: res.data.file_name, profilePicFileChanged: true }));
                  } catch {
                    setFlashToast({ message: t('admin.shared.imageUploadFailed'), variant: 'error' });
                    URL.revokeObjectURL(nextPreview);
                    setProfilePreviewUrl('');
                  }
                })()
                  .finally(() => {
                    setUploading(false);
                  });
              }}
            />

            <button
              className="mfb"
              type="button"
              aria-label={t('admin.shared.upload')}
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              title={uploading ? t('profile.uploading') : t('admin.shared.upload')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
            <button
              className="mfb"
              type="button"
              aria-label={t('profile.delete')}
              style={{ background: 'color-mix(in srgb, var(--red) 10%, var(--surface))' }}
              onClick={() => {
                if (profilePreviewUrl) URL.revokeObjectURL(profilePreviewUrl);
                setProfilePreviewUrl('');
                if (fileInputRef.current) fileInputRef.current.value = '';
                setUser((u) => ({
                  ...u,
                  profilePicFileRef: '',
                  profilePicFileChanged: true,
                  profilePictureIconFullpath: undefined,
                  profilePictureIconUrl: undefined,
                }));
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
              <div className="al-l">
                {t('admin.extranetUserForm.extranetAgencyName')} <span className="req">*</span>
              </div>
              {isEdit ? (
                <input id="extranet-agencyName" className="al-inp" value={user.agencyName} readOnly disabled aria-readonly />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                  <select
                    id="extranet-agencyName"
                    className={alSelClass(user.agencyName)}
                    value={user.agencyName}
                    onChange={(e) => {
                      const nm = e.target.value;
                      const selected = agencyOptions.find((a) => a.name === nm) ?? null;
                      setUser((u) => ({ ...u, agencyName: nm, agencyId: selected?.id }));
                      if (e.target.value.trim()) clearReqError('agencyName');
                    }}
                    style={reqErrors.agencyName ? { borderColor: 'var(--red)' } : undefined}
                    aria-invalid={reqErrors.agencyName ? true : undefined}
                    aria-describedby={reqErrors.agencyName ? 'extranet-agencyName-err' : undefined}
                  >
                    <option value="" disabled hidden>
                      {t('admin.extranetUserForm.selectExtranetAgencyName')}
                    </option>
                    {agencyNames.map((nm) => (
                      <option key={nm} value={nm}>
                        {nm}
                      </option>
                    ))}
                  </select>
                  <button
                    className="mfb mfb-dk"
                    type="button"
                    onClick={() => {
                      setAddAgencyErr(null);
                      setNewAgencyName('');
                      setAddAgencyOpen(true);
                    }}
                  >
                    {t('admin.extranetUserForm.addNewAgency')}
                  </button>
                </div>
              )}
              {!isEdit && reqErrors.agencyName ? (
                <div id="extranet-agencyName-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                  {reqErrors.agencyName}
                </div>
              ) : null}
            </div>
            <FieldInput
              label={t('admin.extranetUserForm.companyCode')}
              value={user.companyCode ?? ''}
              placeholder={t('admin.extranetUserForm.companyCodePlaceholder')}
              maxLength={255}
              error={reqErrors.companyCode}
              onChange={(v) => {
                setUser((u) => ({ ...u, companyCode: v.slice(0, 255) }));
                clearReqError('companyCode');
              }}
            />
            <FieldInput
              label={t('admin.extranetUserForm.brn')}
              value={user.brn ?? ''}
              placeholder={t('admin.extranetUserForm.brnPlaceholder')}
              maxLength={255}
              error={reqErrors.brn}
              onChange={(v) => {
                setUser((u) => ({ ...u, brn: v.slice(0, 255) }));
                clearReqError('brn');
              }}
            />
            <FieldInput
              id="extranet-email"
              fieldKey="email"
              label={t('profile.emailUsername')}
              required
              value={user.email}
              placeholder={t('admin.intranetUserForm.emailPlaceholder')}
              maxLength={255}
              error={reqErrors.email}
              disabled={isEdit}
              onChange={(v) => {
                const capped = v.slice(0, 255);
                const cleaned = capped.trim();
                setUser((u) => ({ ...u, email: capped }));
                if (cleaned) clearReqError('email');
              }}
              hint={t('profile.emailHint')}
            />
            <FieldInput
              id="extranet-contactPersonName"
              fieldKey="contactPersonName"
              label={t('admin.extranetUserForm.agencyUserFullName')}
              required
              value={user.contactPersonName}
              placeholder={t('admin.extranetUserForm.agencyUserFullNamePlaceholder')}
              maxLength={255}
              error={reqErrors.contactPersonName}
              onChange={(v) => {
                const cleaned = v.slice(0, 255);
                setUser((u) => ({ ...u, contactPersonName: cleaned }));
                if (cleaned.trim()) clearReqError('contactPersonName');
              }}
            />
            <FieldInput
              id="extranet-displayName"
              fieldKey="displayName"
              label={t('admin.shared.displayName')}
              value={displayNameToSave}
              placeholder={autoDisplayName || t('admin.extranetUserForm.displayNamePlaceholder')}
              onChange={() => {
                // Intentionally non-editable — auto-generated from Agency User's Full Name.
              }}
              disabled
            />
            <FieldInput
              id="extranet-contactNumber"
              fieldKey="contactNumber"
              label={t('profile.contactNumber')}
              required
              value={user.contactNumber ?? ''}
              placeholder={t('profile.contactPlaceholder')}
              maxLength={10}
              error={reqErrors.contactNumber}
              onChange={(v) => {
                const cleaned = digitsOnlyMax10(v);
                setUser((u) => ({ ...u, contactNumber: cleaned }));
                if (!cleaned) {
                  clearReqError('contactNumber');
                  return;
                }
                const contactErr = contactNumberError(cleaned);
                if (contactErr) {
                  setReqErrors((cur) => ({ ...cur, contactNumber: contactErr }));
                } else {
                  clearReqError('contactNumber');
                }
              }}
            />
            <FieldInput
              label={t('admin.extranetUserForm.agencyUserDesignation')}
              value={user.contactPersonDesignation ?? ''}
              placeholder={t('admin.extranetUserForm.agencyUserDesignationPlaceholder')}
              maxLength={255}
              error={reqErrors.contactPersonDesignation}
              onChange={(v) => {
                setUser((u) => ({ ...u, contactPersonDesignation: v.slice(0, 255) }));
                clearReqError('contactPersonDesignation');
              }}
            />

            <div className="al-f" style={{ justifyContent: 'flex-end' }}>
              <label className="ccr-chk" style={{ marginBottom: 0 }}>
                <input type="checkbox" checked={user.status === 'Active'} onChange={(e) => setUser((u) => ({ ...u, status: e.target.checked ? 'Active' : 'Inactive' }))} />
                <span>{t('common.active')}</span>
              </label>
            </div>
          </div>

          {isEdit ? (
            <SystemInformationSection title={t('profile.systemInformation')}>
              <SystemInfoBox label={t('profile.createdAt')} value={user.createdAt} />
              <SystemInfoBox label={t('profile.createdBy')} value={user.createdBy} />
              <SystemInfoBox label={t('profile.lastUpdatedAt')} value={user.updatedAt} />
              <SystemInfoBox label={t('profile.lastUpdatedBy')} value={user.updatedBy} />
            </SystemInformationSection>
          ) : null}
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <Modal
        open={addAgencyOpen}
        title={t('admin.extranetUserForm.addNewExtranetAgency')}
        onClose={() => {
          setAddAgencyOpen(false);
          setAddAgencyErr(null);
          setNewAgencyName('');
        }}
        boxClassName="cn-mbox-location"
      >
        <FieldInput
          id="add-agency-name"
          fieldKey="addAgencyName"
          label={t('admin.extranetUserForm.extranetAgencyName')}
          required
          value={newAgencyName}
          placeholder={t('admin.extranetUserForm.enterExtranetAgencyName')}
          error={addAgencyErr ?? undefined}
          onChange={(v) => {
            setNewAgencyName(v);
            if (v.trim()) setAddAgencyErr(null);
          }}
        />
        <div className="cn-footer cn-footer--plain">
          <button
            className="mfb"
            type="button"
            disabled={addAgencySaving}
            onClick={() => {
              setAddAgencyOpen(false);
              setAddAgencyErr(null);
              setNewAgencyName('');
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            className="mfb mfb-dk"
            type="button"
            disabled={addAgencySaving}
            onClick={() => {
              const cleaned = newAgencyName.replace(/\s+/g, ' ').trim();
              if (!cleaned) {
                setAddAgencyErr(t('admin.extranetUserForm.agencyNameRequired'));
                return;
              }
              setAddAgencyErr(null);
              setAddAgencySaving(true);
              void (async () => {
                const v = await validateExtranetAgencyName(cleaned);
                if (!v.success) {
                  setAddAgencyErr(v.message);
                  return;
                }

                const created = await createExtranetAgency(cleaned);
                if (!created.success) {
                  setAddAgencyErr(created.message);
                  return;
                }

                const list = await listExtranetAgencies();
                if (list.success) setAgencyOptions(list.data);

                setUser((u) => ({
                  ...u,
                  agencyName: created.data.name,
                  agencyId: created.data.id || u.agencyId,
                }));
                clearReqError('agencyName');
                setAddAgencyOpen(false);
                setNewAgencyName('');
              })().finally(() => setAddAgencySaving(false));
            }}
          >
            {addAgencySaving ? t('admin.shared.adding') : t('admin.shared.add')}
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('admin.shared.cancelChangesMessage')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/admin/extranet-users');
        }}
      />
    </>
  );
}

function FieldInput({
  id,
  fieldKey,
  label,
  required,
  value,
  placeholder,
  onChange,
  hint,
  error,
  disabled,
  maxLength,
}: {
  id?: string;
  fieldKey?: string;
  label: string;
  required?: boolean;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
  hint?: string;
  error?: string;
  disabled?: boolean;
  maxLength?: number;
}) {
  return (
    <div className="al-f">
      <div className="al-l">
        {label} {required ? <span className="req">*</span> : null}
      </div>
      <input
        id={id}
        className="al-inp"
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={error ? { borderColor: 'var(--red)' } : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id ?? fieldKey ?? label}-err` : undefined}
      />
      {error ? (
        <div id={`${id ?? fieldKey ?? label}-err`} style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
          {error}
        </div>
      ) : null}
      {hint ? <div style={{ fontSize: 11, color: 'var(--t3)' }}>{hint}</div> : null}
    </div>
  );
}


